﻿import { QueryClient, useInfiniteQuery, useMutation, useQuery } from '@tanstack/react-query';
import axios from 'axios';

import { ALL_PLAYLISTS_QUERY_KEY, PLAYLIST_API_URL, PLAYLIST_BY_ID_QUERY_KEY } from '@/constants';
import { PlaylistType } from '@/enums';
import { apiUrl, getQueryParams } from '@/helpers';
import { CreateEditPlaylistPayload, ErrorResponse, Options, UpdateBasicPlaylist } from '@/types';

export const playlistEndpoints = {
  getAllPlaylists: (
    pageSize: number,
    cursor: number,
    searchQuery?: string,
    sortingField?: string,
    sortingAsc?: boolean,
    organizationId?: number,
    playlistType?: PlaylistType,
    deviceId?: number
  ) =>
    axios.get(
      `${apiUrl()}${PLAYLIST_API_URL}/all/${getQueryParams({
        'paging.PageSize': pageSize as number,
        'paging.Cursor': cursor as number,
        'sorting.Field': sortingField as string,
        'sorting.Asc': sortingAsc as boolean,
        'search.Text': searchQuery as string,
        'filter.OrganizationId': organizationId as number,
        'filter.PlaylistType': playlistType as PlaylistType,
        'filter.DeviceId': deviceId as number
      })}`
    ),
  getById: (id: number) => axios.get(`${apiUrl()}${PLAYLIST_API_URL}/${id}`),
  deletePlaylist: (id: number) => axios.delete(`${apiUrl()}${PLAYLIST_API_URL}/${id}`),
  createPlaylist: (payload: CreateEditPlaylistPayload) =>
    axios.post(`${apiUrl()}${PLAYLIST_API_URL}`, payload),
  updatePlaylist: (payload: CreateEditPlaylistPayload) =>
    axios.put(`${apiUrl()}${PLAYLIST_API_URL}`, payload),
  updateBasicPlaylist: (payload: UpdateBasicPlaylist) =>
    axios.patch(`${apiUrl()}${PLAYLIST_API_URL}/update_playlist`, payload)
};

export const useGetAllPlaylists = (
  pageSize: number,
  searchQuery?: string,
  sortingField?: string,
  sortingAsc?: boolean,
  organizationId?: number,
  playlistType?: PlaylistType,
  enabled?: boolean,
  deviceId?: number
) => {
  return useInfiniteQuery(
    [
      ALL_PLAYLISTS_QUERY_KEY,
      { pageSize, searchQuery, sortingField, sortingAsc, organizationId, playlistType }
    ],
    ({ pageParam }) =>
      playlistEndpoints.getAllPlaylists(
        pageSize,
        pageParam,
        searchQuery,
        sortingField,
        sortingAsc,
        organizationId,
        playlistType,
        deviceId
      ),
    {
      keepPreviousData: true,
      enabled: enabled ?? true,
      refetchOnMount: 'always',
      cacheTime: 0,
      getNextPageParam: (lastPage) => {
        if (lastPage && lastPage.data?.nextCursor !== undefined) {
          return lastPage.data?.nextCursor;
        }
        return undefined;
      }
    }
  );
};

export const useGetPlaylistById = (id: number, options?: Options) => {
  return useQuery([PLAYLIST_BY_ID_QUERY_KEY, { id }], () => playlistEndpoints.getById(id), {
    enabled: !!id,
    retry: false,
    onSuccess: () => {
      options?.onSuccess();
    },
    onError: (error) => {
      options?.onError(error as ErrorResponse);
    }
  });
};

export const useDeletePlaylistMutation = (queryClient: QueryClient, options: Options) => {
  return useMutation(playlistEndpoints.deletePlaylist, {
    onSuccess: () => {
      options?.onSuccess();
      queryClient.invalidateQueries([ALL_PLAYLISTS_QUERY_KEY]);
    },
    onError: (error) => {
      options?.onError(error as ErrorResponse);
    }
  });
};

export const useCreatePlaylistMutation = (queryClient: QueryClient, options: Options) => {
  return useMutation(playlistEndpoints.createPlaylist, {
    onSuccess: (response) => {
      options.onSuccess(response);
      queryClient.invalidateQueries([ALL_PLAYLISTS_QUERY_KEY]);
    },
    onError: (error) => {
      options.onError(error as ErrorResponse);
    }
  });
};

export const useUpdatePlaylistMutation = (queryClient: QueryClient, options: Options) => {
  return useMutation(playlistEndpoints.updatePlaylist, {
    onSuccess: () => {
      options.onSuccess();
      queryClient.invalidateQueries([PLAYLIST_BY_ID_QUERY_KEY]);
    },
    onError: (error) => {
      options.onError(error as ErrorResponse);
    }
  });
};

export const useUpdateBasicPlaylistMutation = (queryClient: QueryClient, options: Options) => {
  return useMutation(playlistEndpoints.updateBasicPlaylist, {
    onSuccess: (response) => {
      options.onSuccess(response);
      queryClient.invalidateQueries([PLAYLIST_BY_ID_QUERY_KEY]);
    },
    onError: (error) => {
      options.onError(error as ErrorResponse);
    }
  });
};
